import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Users, Upload, Image, Shield, FolderKanban } from 'lucide-react';

const projectColors = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#ef4444',
  '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6'
];

function CreateProjectModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: [],
    color: projectColors[0],
    type: 'standard',
  });
  const [users, setUsers] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const project = await onCreate(formData);
      
      // Upload image if provided
      if (imageFile && project._id) {
        const imageData = new FormData();
        imageData.append('image', imageFile);
        const imageResponse = await axios.put(`/projects/${project._id}/image`, imageData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        // Force parent to refetch projects to get the updated image
        window.location.reload();
      }
      
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const toggleMember = (userId) => {
    setFormData({
      ...formData,
      members: formData.members.includes(userId)
        ? formData.members.filter(id => id !== userId)
        : [...formData.members, userId]
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gray-900)' }}>
              Create New Project
            </h2>
            <button 
              onClick={onClose}
              className="btn btn-ghost"
              style={{ padding: '8px', width: '40px', height: '40px' }}
            >
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="error-message" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>Project type *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: 'standard' })}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '4px',
                    padding: '14px 16px',
                    background: formData.type === 'standard' ? 'rgba(99, 102, 241, 0.06)' : 'var(--bg-surface)',
                    border: formData.type === 'standard' ? '2px solid #6366F1' : '2px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all var(--transition-base)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px' }}>
                    <FolderKanban size={16} color={formData.type === 'standard' ? '#6366F1' : 'currentColor'} />
                    Standard
                  </div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    Kanban board with tasks, columns, comments. Use for general work.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: 'design-workflow' })}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '4px',
                    padding: '14px 16px',
                    background: formData.type === 'design-workflow' ? 'rgba(220, 20, 60, 0.06)' : 'var(--bg-surface)',
                    border: formData.type === 'design-workflow' ? '2px solid var(--accent)' : '2px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all var(--transition-base)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px' }}>
                    <Shield size={16} color={formData.type === 'design-workflow' ? 'var(--accent)' : 'currentColor'} />
                    Design Assurance
                  </div>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    RMAR 21J Design Tasks with state machine, audit log, assignments.
                  </span>
                </button>
              </div>
              {formData.type === 'design-workflow' && (
                <p style={{
                  marginTop: '10px',
                  fontSize: '11.5px',
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}>
                  The project name becomes part of every Design Task identifier
                  (e.g. <code style={{ fontSize: '11px' }}>KDS-DT-{(formData.name || 'name').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toUpperCase() || 'NAME'}-2026-0001</code>).
                  Pick a short, distinctive name — it cannot be changed later without breaking continuity.
                </p>
              )}
            </div>

            <div className="input-group">
              <label>Project name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Enter project name"
                autoFocus
              />
            </div>

            <div className="input-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="What's this project about?"
              />
            </div>

            <div className="input-group">
              <label>Project colour</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {projectColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: 'var(--radius-md)',
                      background: color,
                      border: formData.color === color ? '3px solid var(--gray-900)' : '2px solid var(--gray-200)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-base)',
                      transform: formData.color === color ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={16} />
                Team Members
              </label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '2px solid var(--gray-200)', borderRadius: 'var(--radius-md)', padding: '8px' }}>
                {users.map(user => (
                  <label 
                    key={user._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      transition: 'background var(--transition-base)'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={formData.members.includes(user._id)}
                      onChange={() => toggleMember(user._id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="avatar" style={{ width: '32px', height: '32px', fontSize: '13px' }}>
                      {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{user.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{user.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading} style={{ flex: 1 }}>
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateProjectModal;
